import { createContext, Dispatch, MutableRefObject } from "react";

import { SelectionSet } from "utils/selectionSet";

// Ids are held once, in an immutable SelectionSet (sorted Uint32Array) — see
// the note in contexts/highlighted/reducer.ts. `idArr` is the set's sorted
// backing array; it MUST NOT be mutated.
//
// frozenIds / lastHiddenIds / toggleSnapshot used to be a native Set + two
// number[] snapshots. Hide-all + autofreeze on a ~2.86M-object scene stored
// the same ids three times (~11 MB compact hidden set + ~50–80 MB Set +
// another full array). All three are SelectionSets now (~4 B/id each).

export const initialState = {
    ids: SelectionSet.EMPTY,
    idArr: SelectionSet.EMPTY.sortedIds,
    // Try to not change frozenIds reference because useHandleHighlights relies on it
    frozenIds: SelectionSet.EMPTY,
    lastHiddenIds: SelectionSet.EMPTY,
    toggleSnapshot: SelectionSet.EMPTY,
};

export type State = typeof initialState;

export enum ActionTypes {
    Add,
    Remove,
    SetIds,
    AddFrozen,
    SetFrozen,
    SetLastHiddenIds,
    SetToggleSnapshot,
}

function add(ids: Iterable<number>) {
    return {
        type: ActionTypes.Add as const,
        ids,
    };
}

function remove(ids: Iterable<number>) {
    return {
        type: ActionTypes.Remove as const,
        ids,
    };
}

function setIds(ids: Iterable<number>) {
    return {
        type: ActionTypes.SetIds as const,
        ids,
    };
}

function addFrozen(ids: Iterable<number>) {
    return {
        type: ActionTypes.AddFrozen as const,
        ids,
    };
}

function setFrozen(ids: Iterable<number>) {
    return {
        type: ActionTypes.SetFrozen as const,
        ids,
    };
}

function setLastHiddenIds(ids: Iterable<number>) {
    return {
        type: ActionTypes.SetLastHiddenIds as const,
        ids,
    };
}

function setToggleSnapshot(ids: Iterable<number>) {
    return {
        type: ActionTypes.SetToggleSnapshot as const,
        ids,
    };
}

export const actions = { add, remove, setIds, addFrozen, setFrozen, setLastHiddenIds, setToggleSnapshot };

type Actions = ReturnType<(typeof actions)[keyof typeof actions]>;
export type DispatchHidden = Dispatch<Actions>;
export type LazyState = MutableRefObject<State>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const StateContext = createContext<State>(undefined as any);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const LazyStateContext = createContext<LazyState>(undefined as any);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const DispatchContext = createContext<DispatchHidden>(undefined as any);

export function reducer(state: State, action: Actions): State {
    switch (action.type) {
        case ActionTypes.Add: {
            const ids = state.ids.withAdded(action.ids);

            return {
                ...state,
                ids,
                idArr: ids.sortedIds,
            };
        }
        case ActionTypes.Remove: {
            const ids = state.ids.withRemoved(action.ids);
            // SelectionSet.withRemoved returns `this` when none of the supplied ids were
            // present in the set.  Short-circuit here so that the context does NOT hand a
            // new state object to subscribers — preventing a cascade of re-renders for all
            // useHidden() consumers when the remove was a no-op (e.g. showing objects in
            // isolation mode that were never explicitly hidden by the user).
            if (ids === state.ids) {
                return state;
            }
            const frozenIds = state.frozenIds.withRemoved(action.ids);

            return {
                ...state,
                ids,
                idArr: ids.sortedIds,
                frozenIds,
            };
        }
        case ActionTypes.SetIds: {
            const ids = SelectionSet.fromIds(action.ids);

            return {
                ...state,
                ids,
                idArr: ids.sortedIds,
                // Frozen ids that are no longer hidden are dropped.
                frozenIds: state.frozenIds.intersection(ids),
            };
        }
        case ActionTypes.AddFrozen: {
            const frozenIds = state.frozenIds.withAdded(action.ids);
            if (frozenIds.size === state.frozenIds.size) {
                return state;
            }

            return {
                ...state,
                frozenIds,
            };
        }
        case ActionTypes.SetFrozen: {
            return {
                ...state,
                frozenIds: SelectionSet.fromIds(action.ids),
            };
        }
        case ActionTypes.SetLastHiddenIds: {
            return {
                ...state,
                lastHiddenIds: SelectionSet.fromIds(action.ids),
            };
        }
        case ActionTypes.SetToggleSnapshot: {
            return {
                ...state,
                toggleSnapshot: SelectionSet.fromIds(action.ids),
            };
        }
        default: {
            throw new Error(`Unhandled action type`);
        }
    }
}
